import { Box, Grid, Paper } from "@mui/material";
import * as dunsFilter from "enums/filters/duns";
import * as plantFilter from "enums/filters/plant";
import * as sectorFilter from "enums/filters/sector";
import { motion } from "framer-motion";
import * as hooks from "lib/hooks";
import * as opt from "lib/opt";
import * as result from "lib/result";
import { StepButtonWrapper } from "lib/styles/button";
import { ElevatedPaperStyle } from "lib/styles/shared";
import * as constants from "lib/util";
import { GetContainmentTypeConfig } from "./hooks/containmentTypeConfig";
import {
	UploadContentType,
	DefaultUploadContentTypes,
} from "components/UploadContentTypeSelector";
import {
	DownloadTemplateFile,
	TemplateFileNameForFormat,
	type TemplateFormat,
} from "components/DownloadTemplateMenu";
import type { FileData } from "lib/util/form/types";
import { Sector } from "models";
import { ContainmentHoldDTO } from "models/dto/containmentHold";
import {
	OBJECT_ID_ASRS_LOCK,
	OBJECT_ID_CREATE_CONTAINMENT,
} from "pages/Users/constants";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as routes from "enums/routes";
import {
	PartNumberGateResult,
	ToFlexNetPartNumberGatePayload,
	ToUnsupportedFlexNetPlants,
} from "lib/flexnet/partNumberGate";
import { ContainmentInfoCard, ContainmentTypeCard, SectorsAndPlantsCard, PartNumbersCard, UploadContentTypeCard } from "./cards";
import { useContainmentSectorPermissions } from "./hooks/useContainmentSectorPermissions";
import { StepButtons } from "./StepButtons";

type containmentTypeOption = "flexnet" | "asrs";

interface containmentTypeChoice {
	value: containmentTypeOption;
	label: string;
	containmentTypeName: string;
}

function classifyContainmentType(name: string): containmentTypeOption {
	return name.toUpperCase().includes("ASRS") ? "asrs" : "flexnet";
}


const uploadContentTypes = DefaultUploadContentTypes;

const staggerChildrenVariants = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: { staggerChildren: 0.1 },
	},
};

const childVariants = {
	hidden: { opacity: 0, y: 20 },
	show: {
		opacity: 1,
		y: 0,
		transition: {
			type: "spring" as const,
			stiffness: 400,
			damping: 25,
		},
	},
};

export function PreUpload() {
	const { f, isFormValid } = hooks.UseFormContext();
	const permissions = hooks.UseHasPermission();
	const navigate = useNavigate();
	const mccApi = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();


	// --- Watched form values ---
	const watchedSectors =
		useWatch({ control: f.control, name: "sectors" }) || [];
	const watchedSubsequentHold = useWatch({
		control: f.control,
		name: "containmentSubsequentHold",
	});
	const watchedHasMultipleParts = useWatch({
		control: f.control,
		name: "hasMultipleParts",
	});
	const watchedPlants =
		useWatch({ control: f.control, name: "plants" }) || [];

	// --- Local UI state (derived from form on remount to survive step navigation) ---
	const [containmentType, setContainmentType] =
		useState<containmentTypeOption | null>(() => {
			const sectorTypes = f.getValues("sectorTypes") || [];
			if (sectorTypes.length === 0) return null;
			const hasAsrs = sectorTypes.some(
				(st: any) =>
					st.type?.name?.toUpperCase().includes("ASRS")
			);
			return hasAsrs ? "asrs" : "flexnet";
		});
	const [containmentTypeDbName, setContainmentTypeDbName] = useState<string | undefined>(() => {
		const sectorTypes = f.getValues("sectorTypes") || [];
		if (sectorTypes.length === 0) return undefined;
		const asrsSt = sectorTypes.find((st: any) => st.type?.name?.toUpperCase().includes("ASRS"));
		return asrsSt ? asrsSt?.type?.name : sectorTypes[0]?.type?.name;
	});
	const savedUploadContentTypeLabel = useWatch({
		control: f.control,
		name: "uploadContentType",
	});
	const uploadContentType = useMemo(
		() =>
			uploadContentTypes.find(
				(t) => t.label === savedUploadContentTypeLabel
			) ?? null,
		[savedUploadContentTypeLabel]
	);
	const setUploadContentType = useCallback(
		(val: UploadContentType | null) => {
			f.setValue("uploadContentType", val?.label ?? null);
		},
		[f]
	);
	const [downloading, setDownloading] = useState(false);

	const [apiContainmentTypes, setApiContainmentTypes] = useState<containmentTypeChoice[]>([]);
	useEffect(() => {
		mccApi.ContainmentTypes().then((types) => {
			setApiContainmentTypes(
				types.map((t) => ({
					value: classifyContainmentType(t.name),
					label: t.name,
					containmentTypeName: t.name,
				}))
			);
		});
	}, [mccApi]);

	const containmentTypeOptions = useMemo<containmentTypeChoice[]>(() => {
		const hasC1 = permissions.createContainmentPlantIds.length > 0;
		const hasL1 = permissions.asrsLockPlantIds.length > 0;
		return apiContainmentTypes.filter((t) => {
			if (t.value === "flexnet") return hasC1;
			if (t.value === "asrs") return hasL1;
			return false;
		});
	}, [permissions, apiContainmentTypes]);

	const containmentTypeApiName = containmentTypeDbName;

	const typeConfig = useMemo(
		() => containmentTypeApiName ? GetContainmentTypeConfig(containmentTypeApiName) : undefined,
		[containmentTypeApiName]
	);

	// --- Memoized filter types ---
	const sectorFilterType = useMemo(
		() =>
			containmentTypeApiName
				? sectorFilter.SectorByContainmentTypeField(
						containmentTypeApiName
					)
				: sectorFilter.SectorSearchField(),
		[containmentTypeApiName]
	);
	const sectorsDropdownKey = useMemo(
		() => `sectors-${containmentTypeApiName ?? "all"}`,
		[containmentTypeApiName]
	);
	// Extract plants (incl. flexnetVersion) from sector type data returned by the sectors API.
	const availablePlants = useMemo(() => {
		if (!containmentTypeApiName || watchedSectors.length === 0) return [];
		const plantMap = new Map<string, any>();
		for (const sector of watchedSectors) {
			const stArr: any[] = Array.isArray((sector as any).sectorTypes)
				? (sector as any).sectorTypes
				: [];
			for (const st of stArr) {
				if (st.type?.name !== containmentTypeApiName) continue;
				const plants: any[] = Array.isArray(st.plants) ? st.plants : [];
				for (const plant of plants) {
					if (!plantMap.has(plant.id)) {
						plantMap.set(plant.id, {
							...plant,
							sectorId: sector.id,
							sectorContext: (sector as any).name ?? "Other",
						});
					}
				}
			}
		}
		return Array.from(plantMap.values());
	}, [watchedSectors, containmentTypeApiName]);

	const [partNumberGate, setPartNumberGate] = useState<PartNumberGateResult>(
		() => {
			const required =
				f.getValues("partNumberRequired") === true ||
				Boolean(f.getValues("partNumber")) ||
				Boolean(f.getValues("hasMultipleParts"));
			return { partNumberRequired: required, unsupportedPlants: [] };
		}
	);

	// Server-side FlexNet PN gate whenever impacted plant selection changes (SITMP-7497).
	useEffect(() => {
		if (!typeConfig?.hasHolds) {
			setPartNumberGate({ partNumberRequired: false, unsupportedPlants: [] });
			// ASRS: skip FlexNet PN-gate. Single-column checks use Upload Content Type.
			f.setValue("partNumberRequired", null);
			return;
		}
		const plants = (Array.isArray(watchedPlants) ? watchedPlants : []).map(
			ToFlexNetPartNumberGatePayload
		);
		if (plants.length === 0) {
			setPartNumberGate({ partNumberRequired: false, unsupportedPlants: [] });
			f.setValue("partNumberRequired", null);
			return;
		}
		let cancelled = false;
		mccApi
			.EvaluateFlexNetPartNumberGate(plants)
			.then((unsupported) => {
				if (cancelled) return;
				const unsupportedPlants = ToUnsupportedFlexNetPlants(unsupported);
				const partNumberRequired = unsupportedPlants.length > 0;
				setPartNumberGate({
					partNumberRequired,
					unsupportedPlants,
				});
				// Drive frontend upload column layout from the gate (SITMP-7497).
				f.setValue("partNumberRequired", partNumberRequired);
			})
			.catch(() => {
				// Fail closed: keep PN required if the gate API is unavailable.
				if (!cancelled) {
					setPartNumberGate({
						partNumberRequired: true,
						unsupportedPlants: [],
					});
					f.setValue("partNumberRequired", true);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [typeConfig?.hasHolds, watchedPlants, mccApi, f]);

	// When the gate drops Part Number requirement, clear intake PN values (SITMP-7497 AC).
	// Only on a true → false transition. PreUpload remounts on Back / Start Over with
	// partNumberRequired initialized false until the gate API returns, which used to
	// wipe filled PN + DUNS (SITMP-8267).
	const prevGateRequiredRef = useRef<boolean | undefined>(undefined);
	useEffect(() => {
		if (!typeConfig?.hasHolds) return;
		const prev = prevGateRequiredRef.current;
		prevGateRequiredRef.current = partNumberGate.partNumberRequired;
		if (prev !== true || partNumberGate.partNumberRequired) return;
		const currentPn = f.getValues("partNumber");
		const currentMulti = f.getValues("hasMultipleParts");
		if (currentPn || currentMulti) {
			f.setValue("partNumber", "");
			f.setValue("hasMultipleParts", false);
			f.setValue("duns", opt.None());
			f.clearErrors("partNumber");
			f.clearErrors("duns");
		}
	}, [partNumberGate.partNumberRequired, typeConfig?.hasHolds, f]);

	// If the PN gate flips after a file was chosen, drop it so column layout is re-checked.
	const watchedPartNumberRequired = f.watch("partNumberRequired");
	const prevPartNumberRequired = useRef(watchedPartNumberRequired);
	useEffect(() => {
		const prev = prevPartNumberRequired.current;
		prevPartNumberRequired.current = watchedPartNumberRequired;
		if (prev === watchedPartNumberRequired) return;
		if (prev == null || watchedPartNumberRequired == null) return;
		if (f.getValues("file").IsNone()) return;
		f.setValue("file", opt.None());
		f.setValue("parsed", []);
		f.clearErrors("file");
	}, [watchedPartNumberRequired, f]);

	const plantFilterType = useMemo(() => plantFilter.PlantStaticField(), []);

	const unauthorizedAsrsPlants = useMemo(() => {
		if (!typeConfig?.autoSelectAllPlants || availablePlants.length === 0) return [];
		const allowedPlantIds = new Set(permissions.asrsLockPlantIds);
		return availablePlants.filter((p: any) => !allowedPlantIds.has(p.id));
	}, [typeConfig, availablePlants, permissions.asrsLockPlantIds]);
	const dunsFilterType = useMemo(() => dunsFilter.Dropdown(), []);

	const hasSectorSelected = watchedSectors && watchedSectors.length > 0;
	const hasPlantSelected = Array.isArray(watchedPlants) && watchedPlants.length > 0;
	const hasRESSSector = useMemo(
		() => watchedSectors.some((s: any) => s.name === constants.RESS),
		[watchedSectors]
	);

	const {
		getHoldOptionDisabled,
		getImpactedPlantOptionDisabled,
		getSectorOptionDisabled,
		handleSubsequentHoldChange,
	} = useContainmentSectorPermissions({
		form: f,
		permissions,
		watchedSectors,
		watchedSubsequentHold,
		containmentTypeName: containmentTypeApiName,
		typeConfig,
	});

	// Capture the subsequent-hold options once loaded so the parent effect
	// below can auto-select the single permitted option. We deliberately do NOT
	// setValue from here (the dropdown's data callback): react-hook-form's
	// controlled subscription for the field is established in a parent effect
	// that runs after this child callback, so a value set here would be missed.
	const [subsequentHoldOptions, setSubsequentHoldOptions] = useState<
		ContainmentHoldDTO[]
	>([]);
	const handleSubsequentDataLoad = useCallback(
		(options: ContainmentHoldDTO[] | null | undefined) => {
			setSubsequentHoldOptions(options ?? []);
		},
		[]
	);

	// Auto-select the subsequent hold when exactly one option is permitted for
	// the current sector/permission context. Re-evaluates when permissions
	// change (e.g. after the sector auto-selects) since option availability is
	// permission-dependent.
	useEffect(() => {
		// Never auto-select a hold for a containment type that has no holds
		// (e.g. ASRS). Without this guard, after handleContainmentTypeChange
		// clears containmentSubsequentHold to None, this effect would re-run
		// and re-select the single enabled option, reintroducing a Some hold
		// that buildSubmitContainmentPayload then leaks into the payload.
		if (!typeConfig?.hasHolds) return;
		if (subsequentHoldOptions.length === 0) return;
		const current = f.getValues("containmentSubsequentHold");
		const alreadySet = opt.IsOpt(current)
			? current.IsSome()
			: current != null;
		if (alreadySet) return;

		const enabledOptions = subsequentHoldOptions.filter(
			(o) => !getHoldOptionDisabled(o)
		);
		if (enabledOptions.length === 1) {
			f.setValue(
				"containmentSubsequentHold",
				opt.FromNullable(enabledOptions[0]),
				{ shouldValidate: true }
			);
		}
	}, [typeConfig?.hasHolds, subsequentHoldOptions, getHoldOptionDisabled, f]);

	const lastSectorIdsRef = useRef(
		(Array.isArray(f.getValues("sectors")) ? f.getValues("sectors") : [])
			.map((s: { id?: string }) => s?.id)
			.filter(Boolean)
			.sort()
			.join(",")
	);

	// --- Containment type change handler (resets Card 3 + 4) ---
	const handleContainmentTypeChange = useCallback(
		(type: containmentTypeOption) => {
			if (type === containmentType) return;
			setContainmentType(type);
			const selected = apiContainmentTypes.find((t) => t.value === type);
			setContainmentTypeDbName(selected?.containmentTypeName);

			// Reset sectors, plants, and dependent fields
			f.setValue("sectors", []);
			lastSectorIdsRef.current = "";
			f.setValue("sectorTypes", []);
			f.setValue("plants", []);
			f.setValue("partNumber", "");
			f.setValue("duns", opt.None());
			f.setValue("hasMultipleParts", false);
			f.clearErrors("sectors");
			f.clearErrors("plants");
			f.clearErrors("partNumber");
			f.clearErrors("duns");
			setUploadContentType(null);

			const newConfig = selected ? GetContainmentTypeConfig(selected.containmentTypeName) : undefined;
			if (!newConfig?.hasHolds) {
				f.setValue("containmentInitialHold", opt.None());
				f.setValue("containmentSubsequentHold", opt.None());
			}
		},
		[containmentType, f, apiContainmentTypes]
	);

	// --- Revalidate plants when sectors change ---
	useEffect(() => {
		void f.trigger("plants");
	}, [f, watchedSectors]);

	// Clear plants when all sectors are removed (tag clear / deselect).
	useEffect(() => {
		if (watchedSectors.length > 0) return;
		if (!Array.isArray(watchedPlants) || watchedPlants.length === 0) return;
		f.setValue("plants", []);
		f.clearErrors("plants");
	}, [watchedSectors, watchedPlants, f]);

	// Keep selected plants valid when available plants change
	useEffect(() => {
		if (watchedSectors.length === 0 || availablePlants.length === 0) return;
		if (Array.isArray(watchedPlants) && watchedPlants.length > 0) {
			const availableIds = new Set(availablePlants.map((p: any) => p.id));
			const validImpactedPlants = watchedPlants.filter(
				(impactedPlant) =>
					availableIds.has(impactedPlant.id) &&
					!getImpactedPlantOptionDisabled(impactedPlant)
			);
			if (validImpactedPlants.length !== watchedPlants.length) {
				f.setValue("plants", validImpactedPlants);
			}
		}
	}, [
		availablePlants,
		f,
		getImpactedPlantOptionDisabled,
		watchedPlants,
		watchedSectors,
	]);

	// Auto-select all plants when type requires it (e.g. ASRS broadcast)
	useEffect(() => {
		if (!typeConfig?.autoSelectAllPlants || availablePlants.length === 0) return;
		const allPlants = availablePlants.map(({ sectorContext, ...plant }) => plant);
		f.setValue("plants", allPlants, { shouldValidate: true });
	}, [typeConfig, availablePlants, f]);

	// Auto-select the sole enabled plant per selected sector (manual plant pick).
	// Runs per sector so adding Transmission after Engine still auto-picks
	// Transmission's only accessible plant even when Engine plants are already selected.
	//
	// This MUST live in the parent screen effect (not the dropdown's
	// `onDataLoaded`): React flushes child effects before parent effects, so a
	// value set from the DropdownFilter's mount effect fires before the
	// controlled field's react-hook-form subscription is established and is
	// silently missed by the Controller. This effect runs after that
	// subscription exists, so `setValue` is observed and the tag renders.
	useEffect(() => {
		if (typeConfig?.autoSelectAllPlants) return;
		if (availablePlants.length === 0 || watchedSectors.length === 0) return;

		const currentPlants = Array.isArray(f.getValues("plants"))
			? [...f.getValues("plants")]
			: [];
		const selectedIds = new Set(currentPlants.map((p: any) => p.id));
		const toAdd: any[] = [];

		for (const sector of watchedSectors) {
			const enabledForSector = availablePlants.filter(
				(p: any) =>
					p.sectorId === sector.id &&
					!getImpactedPlantOptionDisabled(p)
			);
			if (enabledForSector.length !== 1) continue;
			const { sectorContext, ...plant } = enabledForSector[0];
			if (selectedIds.has(plant.id)) continue;
			toAdd.push(plant);
			selectedIds.add(plant.id);
		}

		if (toAdd.length > 0) {
			f.setValue("plants", [...currentPlants, ...toAdd], {
				shouldValidate: true,
			});
		}
	}, [
		typeConfig,
		availablePlants,
		f,
		getImpactedPlantOptionDisabled,
		watchedSectors,
	]);

	// --- ASRS template download ---
	const handleTemplateDownload = async (format: TemplateFormat) => {
		if (!uploadContentType) return;
		setDownloading(true);
		try {
			await DownloadTemplateFile(
				mccApi,
				TemplateFileNameForFormat(uploadContentType.templateFileName, format)
			);
		} catch {
			snack.open(result.Err("Template download failed"));
		} finally {
			setDownloading(false);
		}
	};

	// --- Sector change handler (resets Card 4 when the sector set actually changes) ---
	const handleSectorChange = useCallback(
		({ value }: { value: any }) => {
			const sectors = value as any as Sector[];
			const nextSectorIds = (Array.isArray(sectors) ? sectors : [])
				.map((s: { id?: string }) => s?.id)
				.filter(Boolean)
				.sort()
				.join(",");
			const sectorsChanged = lastSectorIdsRef.current !== nextSectorIds;
			lastSectorIdsRef.current = nextSectorIds;

			if (sectors && sectors.length > 0) {
				// Auto-select the correct sectorType based on containment type
				const matchedSectorTypes = sectors
					.map((sector: any) => {
						const stArray: any[] = Array.isArray(sector.sectorTypes)
							? sector.sectorTypes
							: [];
						return (
							stArray.find(
								(st: any) => st.type?.name === containmentTypeApiName
							) ?? null
						);
					})
					.filter((st: any) => st !== null);

				f.setValue("sectorTypes", matchedSectorTypes);

				// Drop plants that are not in the newly selected sectors' plant lists
				const allowedPlantIds = new Set<string>();
				for (const sector of sectors) {
					const stArr: any[] = Array.isArray((sector as any).sectorTypes)
						? (sector as any).sectorTypes
						: [];
					for (const st of stArr) {
						if (st.type?.name !== containmentTypeApiName) continue;
						const plants: any[] = Array.isArray(st.plants) ? st.plants : [];
						for (const plant of plants) {
							if (plant?.id) allowedPlantIds.add(plant.id);
						}
					}
				}
				const plantObjectId = typeConfig?.usesLockPermissions
					? OBJECT_ID_ASRS_LOCK
					: OBJECT_ID_CREATE_CONTAINMENT;
				const currentPlants = f.getValues("plants");
				if (Array.isArray(currentPlants) && currentPlants.length > 0) {
					const validPlants = currentPlants.filter((plant: any) => {
						if (!allowedPlantIds.has(plant.id)) return false;
						return sectors.some((sector: any) => {
							const plantIds =
								permissions.getPlantIdsByObjectAndSector(
									plantObjectId,
									sector.name
								);
							return plantIds.includes(plant.id);
						});
					});
					if (validPlants.length !== currentPlants.length) {
						f.setValue("plants", validPlants);
						if (validPlants.length === 0) f.clearErrors("plants");
					}
				}

				if (sectorsChanged) {
					// Clear DUNS if no RESS sector
					const hasRESS = sectors.some(
						(s: any) => s.name === constants.RESS
					);
					if (!hasRESS) {
						f.setValue("duns", opt.None());
						f.clearErrors("duns");
					}

					// Reset Card 4 only when the user actually changed sectors
					f.setValue("partNumber", "");
					f.setValue("hasMultipleParts", false);
					f.clearErrors("partNumber");
					setUploadContentType(null);
				}
			} else {
				f.setValue("sectorTypes", []);
				f.setValue("plants", []);
				f.setValue("duns", opt.None());
				f.setValue("partNumber", "");
				f.setValue("hasMultipleParts", false);
				f.clearErrors("duns");
				f.clearErrors("plants");
				f.clearErrors("partNumber");
				setUploadContentType(null);
			}
		},
		[f, typeConfig, containmentTypeApiName, permissions]
	);

	// --- Sector data loaded handler ---
	const sectorPermissionObjectId = typeConfig?.usesLockPermissions
		? OBJECT_ID_ASRS_LOCK
		: OBJECT_ID_CREATE_CONTAINMENT;

	const permittedSectorNames = useMemo(() => {
		return permissions
			.getAccessibleSectorsForObject(sectorPermissionObjectId)
			.map((s) => s.name);
	}, [permissions, sectorPermissionObjectId]);

	const handleSectorDataLoaded = useCallback(
		(sectors: Sector[]) => {
			const currentSectors = f.getValues("sectors");
			const hasSectors = currentSectors && currentSectors.length > 0;
			if (hasSectors) return;
			if (!sectors || sectors.length === 0) return;

			const permittedSectors = sectors.filter((s) =>
				permittedSectorNames.includes(s.name)
			);

			const toSelect =
				permittedSectors.length === 1
					? permittedSectors[0]
					: sectors.length === 1
						? sectors[0]
						: null;

			if (toSelect) {
				f.setValue("sectors", [toSelect]);

				const typeName = containmentTypeApiName;
				if (typeName) {
					const stArr: any[] = Array.isArray(
						(toSelect as any).sectorTypes
					)
						? (toSelect as any).sectorTypes
						: [];
					const matched = stArr.find(
						(st: any) => st.type?.name === typeName
					);
					f.setValue("sectorTypes", matched ? [matched] : [], {
						shouldDirty: true,
					});
				}
			}
		},
		[containmentTypeApiName, f, permittedSectorNames]
	);

	// --- Cancel handler ---
	const handleCancel = useCallback(() => {
		navigate(routes.Dashboard.Path);
	}, [navigate]);

	// --- Step 1 validation ---
	const nextDisabled = useMemo(() => {
		if (!containmentType) return true;

		// Card 1: name + description
		const card1Fields: (keyof FileData)[] = [
			"containmentName",
			"description",
		];

		if (typeConfig?.hasHolds) {
			const baseFields: (keyof FileData)[] = [
				...card1Fields,
				"containmentInitialHold",
				"containmentSubsequentHold",
				"sectors",
				"sectorTypes",
				"plants",
			];

			// FlexNet version gate: Part Number only when any selected plant is unsupported
			if (!partNumberGate.partNumberRequired) {
				return !isFormValid(baseFields);
			}

			if (watchedHasMultipleParts) {
				return !isFormValid(baseFields);
			}

			const requiredFields: (keyof FileData)[] = [
				...baseFields,
				"partNumber",
				...(hasRESSSector ? ["duns" as keyof FileData] : []),
			];
			return !isFormValid(requiredFields);
		}

		// Non-hold path (e.g. ASRS) — plants are auto-populated
		const baseFields: (keyof FileData)[] = [
			...card1Fields,
			"sectors",
			"sectorTypes",
		];
		if (!isFormValid(baseFields)) return true;
		if (typeConfig?.showUploadContentType && !uploadContentType) return true;
		if (unauthorizedAsrsPlants.length > 0) return true;
		return false;
	}, [
		containmentType,
		typeConfig,
		isFormValid,
		unauthorizedAsrsPlants,
		uploadContentType,
		watchedHasMultipleParts,
		hasRESSSector,
		partNumberGate.partNumberRequired,
	]);

	return (
		<Box
			component={motion.div}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
			sx={{ width: "100%" }}
		>
			<Paper
				elevation={0}
				component={motion.div}
				initial={{ scale: 0.97, y: 10 }}
				animate={{ scale: 1, y: 0 }}
				transition={{
					duration: 0.7,
					ease: [0.25, 1, 0.5, 1],
					delay: 0.1,
				}}
				sx={ElevatedPaperStyle}
			>
				<Grid
					container
					spacing={3}
					component={motion.div}
					variants={staggerChildrenVariants}
					initial="hidden"
					animate="show"
					alignItems="stretch"
				>
					{/* ===== Card 1: Name + Description (top-left) ===== */}
					<Grid
						item
						xs={12}
						md={6}
						component={motion.div}
						variants={childVariants}
						sx={{ display: "flex" }}
					>
						<ContainmentInfoCard control={f.control} showComment />
					</Grid>

					{/* ===== Card 2: Containment Type + Hold Type (top-right) ===== */}
					<Grid
						item
						xs={12}
						md={6}
						component={motion.div}
						variants={childVariants}
						sx={{ display: "flex" }}
					>
						<ContainmentTypeCard
							control={f.control}
							containmentType={containmentType}
							containmentTypeOptions={containmentTypeOptions}
							typeConfig={typeConfig}
							onContainmentTypeChange={(val) =>
								handleContainmentTypeChange(val as containmentTypeOption)
							}
							getHoldOptionDisabled={getHoldOptionDisabled}
							handleSubsequentHoldChange={handleSubsequentHoldChange}
							handleSubsequentDataLoad={handleSubsequentDataLoad}
							autoLoadSubsequentHold
							onInitialDataLoad={(d) =>
								f.setValue("containmentInitialHold", d.First())
							}
						/>
					</Grid>

					{/* ===== Card 3: Impacted Sectors + Plants (bottom-left) ===== */}
					{containmentType && (
						<Grid
							item
							xs={12}
							md={6}
							component={motion.div}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{
								type: "spring",
								stiffness: 400,
								damping: 25,
							}}
							sx={{ display: "flex" }}
						>
							<SectorsAndPlantsCard
								control={f.control}
								typeConfig={typeConfig}
								hasSectorSelected={hasSectorSelected}
								hasPlantSelected={hasPlantSelected}
								sectorsDropdownKey={sectorsDropdownKey}
								sectorFilterType={sectorFilterType}
								plantFilterType={plantFilterType}
								availablePlants={availablePlants}
								containmentTypeApiName={containmentTypeApiName}
								watchedSectors={watchedSectors}
								getSectorOptionDisabled={getSectorOptionDisabled}
								getImpactedPlantOptionDisabled={getImpactedPlantOptionDisabled}
								handleSectorDataLoaded={handleSectorDataLoaded}
								handleSectorChange={handleSectorChange}
								unauthorizedPlantNames={unauthorizedAsrsPlants.map((p: any) => p.name)}
								unsupportedPlants={partNumberGate.unsupportedPlants}
								// Fail closed (with Part) until a plant is selected; then use gate result.
								partNumberRequired={
									!hasPlantSelected || partNumberGate.partNumberRequired
								}
								onPlantChange={({ field, value }: any) => {
									if (value != null) {
										const cleanedPlants = Array.isArray(value)
											? value.map((plant: any) => {
													const { sectorContext, ...plantWithSector } = plant;
													return plantWithSector;
												})
											: value;
										f.setValue(
											field,
											Array.isArray(value) ? cleanedPlants : [cleanedPlants],
											{ shouldValidate: true }
										);
									}
								}}
							plantValidationRules={typeConfig?.plantValidationRequired === false ? undefined : {
								validate: (selectedPlants: any) => {
									const errorMessage = "At least one plant must be added per selected sector.";
									const selectedSectors = f.getValues("sectors") ?? [];
									if (selectedSectors.length === 0) return true;
									const plants = Array.isArray(selectedPlants) ? selectedPlants : [];
									const hasSectorWithoutPlant =
										plants.length === 0 ||
										selectedSectors.some(
											(sector: { id: string }) =>
												!plants.some(
													(plant: { sectorId?: string }) =>
														plant.sectorId === sector.id
												)
										);
									if (hasSectorWithoutPlant) return errorMessage;
									return true;
								},
							}}
							/>
						</Grid>
					)}

					{/* ===== Card 4: Part Numbers (FlexNet, gated) or Upload Content Type (ASRS) ===== */}
					{containmentType && (
						<Grid
							item
							xs={12}
							md={6}
							component={motion.div}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{
								type: "spring",
								stiffness: 400,
								damping: 25,
								delay: 0.1,
							}}
							sx={{ display: "flex" }}
						>
							{typeConfig?.hasHolds ? (
								<PartNumbersCard
									control={f.control}
									hasMultipleParts={watchedHasMultipleParts}
									dunsFilterType={dunsFilterType}
									showDuns={hasRESSSector && partNumberGate.partNumberRequired}
									partNumberRequired={partNumberGate.partNumberRequired}
									visible={partNumberGate.partNumberRequired}
									onPartNumberTypeChange={(val) => {
										if (val) {
											if (val.value) {
												f.setValue("duns", opt.None());
												f.clearErrors("duns");
												f.setValue("partNumber", "");
												f.clearErrors("partNumber");
											}
											f.setValue("hasMultipleParts", val.value);
										}
									}}
								/>
							) : (
								<UploadContentTypeCard
									uploadContentTypes={uploadContentTypes}
									uploadContentType={uploadContentType}
									downloading={downloading}
									onUploadContentTypeChange={setUploadContentType}
									onTemplateDownload={handleTemplateDownload}
								/>
							)}
						</Grid>
					)}
				</Grid>

				{/* ===== Cancel / Next buttons ===== */}
				<Box sx={StepButtonWrapper}>
					<StepButtons
						nextDisabled={nextDisabled}
						showCancel
						onCancel={handleCancel}
					/>
				</Box>
			</Paper>
		</Box>
	);
}
